import {
  IcoArchive,
  IcoCheckCircle,
  IcoChevronUp,
  IcoCircle,
  IcoStar,
  IcoText,
} from "../../../../components/icons";
import type { MemberOption } from "../../../../components/ui/member-picker";
import { TiptapEditor } from "../../../../components/ui/tiptap-editor";
import type { Group, QuestionType } from "../../../../types";
import { FrequencySection } from "./frequency-section";
import { QuestionTypeBadge } from "./question-type-badge";
import { TargetingSection } from "./targeting-section";
import type { Question } from "./types";

type QuestionEditorProps = {
  question: Question;
  index: number;
  groups: Group[];
  members: MemberOption[];
  queuedCount: number;
  onUpdate: (updates: Partial<Question>) => void;
  onArchive: () => void;
  onClose: () => void;
};

export function QuestionEditor({
  question,
  index,
  groups,
  members,
  queuedCount,
  onUpdate,
  onArchive,
  onClose,
}: QuestionEditorProps) {
  return (
    <div class="rounded-xl border border-text-subtle bg-surface">
      <button
        type="button"
        onClick={onClose}
        class="w-full flex items-center gap-3 p-4 hover:bg-surface-hover transition-colors rounded-t-xl"
      >
        <span class="text-sm font-medium text-text-muted">{index + 1}.</span>
        <span class="flex-1 text-left font-medium text-text">
          Edit question
        </span>
        <QuestionTypeBadge
          type={question.type}
          ratingMax={question.ratingMax}
        />
        <IcoChevronUp class="size-5 text-text-muted" />
      </button>

      <div class="px-4 pb-4 flex flex-col gap-4">
        <div class="flex flex-col gap-1">
          <span class="text-sm font-medium text-text-secondary">
            Question <span class="text-rose-500">*</span>
          </span>
          <div class="rounded-lg border border-border bg-surface text-text overflow-hidden hover:border-text-subtle focus-within:ring-2 focus-within:ring-ring transition-colors">
            <TiptapEditor
              value={question.title}
              onChange={(html) => onUpdate({ title: html })}
              placeholder="Enter your question..."
              autoFocus={!question.title}
            />
          </div>
        </div>

        <div class="flex flex-col gap-1">
          <span class="text-sm font-medium text-text-secondary">
            Description <span class="text-text-muted">(optional)</span>
          </span>
          <div class="rounded-lg border border-border bg-surface text-text overflow-hidden hover:border-text-subtle focus-within:ring-2 focus-within:ring-ring transition-colors">
            <TiptapEditor
              value={question.description}
              onChange={(html) => onUpdate({ description: html })}
              placeholder="Add helpful context or instructions..."
            />
          </div>
        </div>

        <QuestionTypePicker
          value={question.type}
          onChange={(type) => onUpdate({ type })}
        />

        {question.type === "rating" && (
          <RatingMaxPicker
            value={question.ratingMax}
            onChange={(ratingMax) => onUpdate({ ratingMax })}
          />
        )}

        <TargetingSection
          targetType={question.targetType}
          targetIds={question.targetIds}
          groups={groups}
          members={members}
          onChange={(targetType, targetIds) =>
            onUpdate({ targetType, targetIds })
          }
        />

        <FrequencySection
          frequency={question.frequency}
          category={question.category}
          onChange={(frequency) => onUpdate({ frequency })}
          onCategoryChange={(category) => onUpdate({ category })}
          queuedCount={queuedCount}
        />

        {/* Question settings */}
        <div class="flex flex-col gap-3">
          <span class="text-sm font-medium text-text-secondary">Settings</span>

          <label class="flex items-center gap-2 cursor-pointer group">
            <input
              type="checkbox"
              checked={question.required}
              onChange={(e) =>
                onUpdate({ required: (e.target as HTMLInputElement).checked })
              }
              class="size-4 rounded border-border text-[var(--brand-600)] focus:ring-ring"
            />
            <span class="text-sm text-text group-hover:text-text-secondary transition-colors">
              Required question
            </span>
          </label>

          {question.type === "short_answer" && (
            <label class="flex items-center gap-2 cursor-pointer group">
              <input
                type="checkbox"
                checked={question.allowMultiple}
                onChange={(e) =>
                  onUpdate({
                    allowMultiple: (e.target as HTMLInputElement).checked,
                  })
                }
                class="size-4 rounded border-border text-[var(--brand-600)] focus:ring-ring"
              />
              <span class="text-sm text-text group-hover:text-text-secondary transition-colors">
                Allow multiple answers
              </span>
            </label>
          )}
        </div>

        <div class="pt-2 border-t border-border-muted">
          <button
            type="button"
            onClick={onArchive}
            class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-text-muted hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-900/20 transition-colors"
          >
            <IcoArchive class="size-4" />
            Archive
          </button>
        </div>
      </div>
    </div>
  );
}

type QuestionTypePickerProps = {
  value: QuestionType;
  onChange: (type: QuestionType) => void;
};

function QuestionTypePicker({ value, onChange }: QuestionTypePickerProps) {
  return (
    <fieldset class="flex flex-col gap-1">
      <legend class="text-sm font-medium text-text-secondary">Type</legend>
      <div class="flex gap-3 mt-1">
        <TypeOption
          name="type"
          typeValue="short_answer"
          checked={value === "short_answer"}
          onChange={() => onChange("short_answer")}
          label="Short answer"
          description="Free-form text response"
          icon={<IcoText class="size-5" />}
        />
        <TypeOption
          name="type"
          typeValue="rating"
          checked={value === "rating"}
          onChange={() => onChange("rating")}
          label="Rating"
          description="Scale from 1 to N"
          icon={<IcoStar class="size-5" />}
        />
      </div>
    </fieldset>
  );
}

type TypeOptionProps = {
  name: string;
  typeValue: string;
  checked: boolean;
  onChange: () => void;
  label: string;
  description: string;
  icon: preact.JSX.Element;
};

function TypeOption({
  name,
  typeValue,
  checked,
  onChange,
  label,
  description,
  icon,
}: TypeOptionProps) {
  return (
    <label
      class={`flex-1 flex gap-3 items-start rounded-lg border p-3 cursor-pointer transition-colors focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 focus-within:ring-offset-ring-offset ${
        checked
          ? "border-text-subtle"
          : "border-border hover:border-text-subtle"
      }`}
    >
      <input
        type="radio"
        name={name}
        value={typeValue}
        checked={checked}
        onChange={onChange}
        class="sr-only"
      />
      <div class={`mt-0.5 ${checked ? "text-text" : "text-text-subtle"}`}>
        {checked ? (
          <IcoCheckCircle class="size-5" />
        ) : (
          <IcoCircle class="size-5" />
        )}
      </div>
      <span class="flex flex-col gap-0.5">
        <span class="flex items-center gap-2">
          <span class={checked ? "text-text" : "text-text-muted"}>{icon}</span>
          <span class="font-medium text-text">{label}</span>
        </span>
        <span class="text-sm text-text-muted">{description}</span>
      </span>
    </label>
  );
}

type RatingMaxPickerProps = {
  value: number;
  onChange: (max: number) => void;
};

function RatingMaxPicker({ value, onChange }: RatingMaxPickerProps) {
  const options = [3, 4, 5, 7, 10];

  return (
    <div class="flex flex-col gap-1">
      <span class="text-sm font-medium text-text-secondary">
        Rating scale (1 to N)
      </span>
      <div class="flex gap-2 mt-1">
        {options.map((opt) => (
          <button
            key={opt}
            type="button"
            onClick={() => onChange(opt)}
            class={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              value === opt
                ? "bg-text text-surface"
                : "bg-surface-muted text-text-muted hover:bg-surface-hover hover:text-text"
            }`}
          >
            1-{opt}
          </button>
        ))}
      </div>
    </div>
  );
}
